import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  InnocodeButton,
  InnocodeDatatable,
  InnocodeDropdown,
  InnocodeInput,
  useDialog,
  useInnocodeLoadingOverlay,
} from "@intsoft/innocode-core";
import { useTenantList } from "../../utils/service/tenantList";
import { ModalContext } from "../../utils/modal/ModalProvider";
import DetailModal from "../../utils/modal/DetailModal";
import { ssoAdminApi } from "./api";
import styles from "./ServiceGroups/ServiceGroups.module.css";
import TenantFederationRequestModal from "./modal/TenantFederationRequestModal";

const PAGE_SIZE = 10;
const STATUS_ALL = "ALL";

const statusOptions = [
  { value: STATUS_ALL, label: "전체 상태" },
  { value: "PENDING", label: "요청 대기" },
  { value: "APPROVED", label: "승인됨" },
  { value: "REJECTED", label: "거절됨" },
];

const formatStatusLabel = (status) => {
  if (status === "PENDING") return "요청 대기";
  if (status === "APPROVED") return "승인됨";
  if (status === "REJECTED") return "거절됨";
  return status || "-";
};

const getStatusClassName = (status) => {
  if (status === "APPROVED") {
    return `${styles.statusText} ${styles.statusActive}`;
  }
  if (status === "PENDING") {
    return `${styles.statusText} ${styles.statusInactive}`;
  }
  if (status === "REJECTED") {
    return `${styles.statusText} ${styles.statusDeleted}`;
  }
  return styles.statusText;
};

const isFederationGroup = (group) =>
  String(group?.groupType || "")
    .trim()
    .toUpperCase() === "FEDERATION";

const formatDateTime = (value) => {
  if (value == null || value === "") {
    return "-";
  }

  const numericValue = Number(value);
  const date = Number.isFinite(numericValue)
    ? new Date(numericValue)
    : new Date(value);

  if (Number.isNaN(date.getTime())) {
    return String(value);
  }

  return date.toLocaleString("ko-KR");
};

const normalizeFederation = (federation, fallbackGroup) => ({
  federationId: federation?.federationId || federation?.id || "",
  sourceTenantId: federation?.sourceTenantId || fallbackGroup?.ownerTenantId || "",
  targetTenantId: federation?.targetTenantId || federation?.targetTenant || "",
  sourceTenantName: federation?.sourceTenantName || federation?.sourceTenantLabel || "",
  targetTenantName: federation?.targetTenantName || federation?.targetTenantLabel || "",
  status: federation?.status || "PENDING",
  requestedAt:
    federation?.requestedAt || federation?.createdAt || federation?.requestAt || "",
  groupId: federation?.groupId || fallbackGroup?.groupId || "",
  groupName: federation?.groupName || fallbackGroup?.groupName || "",
  groupKey: federation?.groupKey || fallbackGroup?.groupKey || "",
  reason: federation?.reason || "",
  approvable: Boolean(federation?.approvable),
  rejectable: Boolean(federation?.rejectable),
});

const dedupeFederations = (items) => {
  const federationMap = new Map();

  items.forEach((item) => {
    if (!item.federationId) {
      return;
    }

    federationMap.set(item.federationId, item);
  });

  return Array.from(federationMap.values());
};

function TenantFederationsPage() {
  const { tenantList } = useTenantList();
  const { openModal, closeModal } = useContext(ModalContext);
  const { showDialog } = useDialog();
  const { show: showLoading, hide: hideLoading } = useInnocodeLoadingOverlay();
  const [federations, setFederations] = useState([]);
  const [status, setStatus] = useState(STATUS_ALL);
  const [sourceKeyword, setSourceKeyword] = useState("");
  const [targetKeyword, setTargetKeyword] = useState("");
  const [page, setPage] = useState(0);

  const tenantMetaMap = useMemo(
    () =>
      tenantList.reduce((acc, tenant) => {
        acc[tenant.tenantId] = tenant;
        return acc;
      }, {}),
    [tenantList]
  );

  const resolveTenantLabel = useCallback(
    (tenantId, tenantName = "") =>
      tenantName ||
      tenantMetaMap[tenantId]?.tenantName ||
      tenantMetaMap[tenantId]?.name ||
      tenantId ||
      "-",
    [tenantMetaMap]
  );

  const fallbackLoadFederations = useCallback(async () => {
    const allFederations = [];

    for (const tenant of tenantList) {
      const groupResult = await ssoAdminApi.fetchServiceGroups({
        ownerTenantId: tenant.tenantId,
        groupType: "FEDERATION",
        page: 0,
        size: 100,
      });

      const groups = Array.isArray(groupResult?.content)
        ? groupResult.content
        : Array.isArray(groupResult?.items)
        ? groupResult.items
        : Array.isArray(groupResult?.data)
        ? groupResult.data
        : Array.isArray(groupResult)
        ? groupResult
        : [];

      const federationGroups = groups.filter((group) => isFederationGroup(group));

      for (const group of federationGroups) {
        const detail = await ssoAdminApi.fetchServiceGroupDetail(group.groupId);
        const requests =
          detail?.federations ||
          detail?.federationRequests ||
          detail?.federationPolicies ||
          [];

        if (!Array.isArray(requests)) {
          continue;
        }

        requests.forEach((item) => {
          allFederations.push(
            normalizeFederation(item, {
              groupId: detail.groupId || group.groupId,
              groupKey: detail.groupKey || group.groupKey,
              groupName: detail.groupName || group.groupName,
              ownerTenantId: detail.ownerTenantId || group.ownerTenantId,
            })
          );
        });
      }
    }

    return dedupeFederations(allFederations);
  }, [tenantList]);

  const loadFederations = useCallback(async () => {
    showLoading();

    try {
      let result;

      try {
        const commonParams = {
          ...(status !== STATUS_ALL ? { status } : {}),
          ...(sourceKeyword.trim()
            ? { sourceTenantId: sourceKeyword.trim() }
            : {}),
          ...(targetKeyword.trim()
            ? { targetTenantId: targetKeyword.trim() }
            : {}),
          page: 0,
          size: 200,
        };

        const [allResult, pendingResult] = await Promise.allSettled([
          ssoAdminApi.fetchFederations(commonParams),
          ssoAdminApi.fetchPendingFederations(commonParams),
        ]);

        const allItems = Array.isArray(allResult.value?.content)
          ? allResult.value.content
          : Array.isArray(allResult.value?.items)
          ? allResult.value.items
          : Array.isArray(allResult.value?.data)
          ? allResult.value.data
          : Array.isArray(allResult.value)
          ? allResult.value
          : [];
        const pendingItems = Array.isArray(pendingResult.value?.content)
          ? pendingResult.value.content
          : Array.isArray(pendingResult.value?.items)
          ? pendingResult.value.items
          : Array.isArray(pendingResult.value?.data)
          ? pendingResult.value.data
          : Array.isArray(pendingResult.value)
          ? pendingResult.value
          : [];

        result = dedupeFederations([...allItems, ...pendingItems].map((item) => normalizeFederation(item)));
      } catch (_error) {
        result = await fallbackLoadFederations();
      }

      const items = Array.isArray(result?.content)
        ? result.content
        : Array.isArray(result?.items)
        ? result.items
        : Array.isArray(result?.data)
        ? result.data
        : Array.isArray(result)
        ? result
        : [];

      setFederations(items.map((item) => normalizeFederation(item)));
    } catch (error) {
      setFederations([]);
      showDialog({
        iconType: "warning",
        type: "error",
        title: "목록 조회 실패",
        message:
          error?.response?.data?.message ||
          "테넌트 연합 목록을 불러오지 못했습니다.",
        confirmText: "확인",
      });
    } finally {
      hideLoading();
    }
  }, [
    fallbackLoadFederations,
    hideLoading,
    showDialog,
    showLoading,
    sourceKeyword,
    status,
    targetKeyword,
    tenantList,
  ]);

  useEffect(() => {
    loadFederations();
  }, [loadFederations]);

  useEffect(() => {
    setPage(0);
  }, [sourceKeyword, status, targetKeyword]);

  const filteredFederations = useMemo(() => {
    return federations.filter((item) => {
      const matchesStatus = status === STATUS_ALL || item.status === status;
      const sourceTenantLabel = resolveTenantLabel(
        item.sourceTenantId,
        item.sourceTenantName
      );
      const targetTenantLabel = resolveTenantLabel(
        item.targetTenantId,
        item.targetTenantName
      );
      const matchesSource =
        !sourceKeyword.trim() ||
        [item.sourceTenantId, sourceTenantLabel]
          .filter(Boolean)
          .some((value) =>
            String(value).toLowerCase().includes(sourceKeyword.trim().toLowerCase())
          );
      const matchesTarget =
        !targetKeyword.trim() ||
        [item.targetTenantId, targetTenantLabel]
          .filter(Boolean)
          .some((value) =>
            String(value).toLowerCase().includes(targetKeyword.trim().toLowerCase())
          );

      return matchesStatus && matchesSource && matchesTarget;
    });
  }, [federations, resolveTenantLabel, sourceKeyword, status, targetKeyword]);

  const pagedFederations = useMemo(() => {
    const startIndex = page * PAGE_SIZE;
    return filteredFederations.slice(startIndex, startIndex + PAGE_SIZE);
  }, [filteredFederations, page]);

  const handleApprove = useCallback(
    async (federationId) => {
      showLoading();

      try {
        await ssoAdminApi.approveFederation(federationId);
        await loadFederations();
      } catch (error) {
        showDialog({
          iconType: "warning",
          type: "error",
          title: "승인 실패",
          message:
            error?.response?.data?.message ||
            "연합 승인 처리 중 오류가 발생했습니다.",
          confirmText: "확인",
        });
      } finally {
        hideLoading();
      }
    },
    [hideLoading, loadFederations, showDialog, showLoading]
  );

  const handleReject = useCallback(
    async (federationId) => {
      showLoading();

      try {
        await ssoAdminApi.rejectFederation(federationId, {
          reason: "rejected by owner",
        });
        await loadFederations();
      } catch (error) {
        showDialog({
          iconType: "warning",
          type: "error",
          title: "거절 실패",
          message:
            error?.response?.data?.message ||
            "연합 거절 처리 중 오류가 발생했습니다.",
          confirmText: "확인",
        });
      } finally {
        hideLoading();
      }
    },
    [hideLoading, loadFederations, showDialog, showLoading]
  );

  const openRequestModal = useCallback(() => {
    const modalContent = (
      <TenantFederationRequestModal
        tenantOptions={tenantList.map((tenant) => ({
          value: tenant.tenantId,
          label: tenant.tenantName || tenant.tenantId,
          tenantName: tenant.tenantName || tenant.tenantId,
          tenantImgUrl: tenant.tenantImgUrl || "",
        }))}
        onClose={closeModal}
        onSaved={loadFederations}
      />
    );

    openModal(
      <DetailModal
        content={modalContent}
        option={{ modalTitle: "테넌트 연합 요청" }}
      />
    );
  }, [closeModal, loadFederations, openModal, tenantList]);

  const columns = useMemo(
    () => [
      {
        id: "sourceTenant",
        header: "요청자",
        accessorKey: "sourceTenantId",
        enableSorting: false,
        cell: ({ row }) => (
          <div className={styles.groupNameCell}>
            <div className={styles.groupName}>
              {resolveTenantLabel(row.sourceTenantId, row.sourceTenantName)}
            </div>
          </div>
        ),
      },
      {
        id: "targetTenant",
        header: "승인 대상",
        accessorKey: "targetTenantId",
        enableSorting: false,
        cell: ({ row }) => (
          <div className={styles.groupNameCell}>
            <div className={styles.groupName}>
              {resolveTenantLabel(row.targetTenantId, row.targetTenantName)}
            </div>
          </div>
        ),
      },
      {
        id: "groupName",
        header: "연합 서비스 그룹",
        accessorKey: "groupName",
        enableSorting: false,
        cell: ({ row }) => (
          <div className={styles.groupNameCell}>
            <div className={styles.groupName}>{row.groupName || "-"}</div>
            <div className={styles.groupKey}>{row.groupKey || row.groupId || "-"}</div>
          </div>
        ),
      },
      {
        id: "requestedAt",
        header: "요청 일시",
        accessorKey: "requestedAt",
        enableSorting: false,
        cell: ({ row }) => <span>{formatDateTime(row.requestedAt)}</span>,
      },
      {
        id: "reason",
        header: "요청 사유",
        accessorKey: "reason",
        enableSorting: false,
        cell: ({ row }) => (
          <span className={styles.selectionMeta}>{row.reason || "-"}</span>
        ),
      },
      {
        id: "status",
        header: "상태",
        accessorKey: "status",
        enableSorting: false,
        cell: ({ row }) => (
          <span className={getStatusClassName(row.status)}>
            {formatStatusLabel(row.status)}
          </span>
        ),
      },
      {
        id: "action",
        header: "처리",
        accessorKey: "action",
        enableSorting: false,
        cell: ({ row }) =>
          row.approvable || row.rejectable ? (
            <div className={styles.inlineButtonRow}>
              {row.approvable ? (
                <InnocodeButton
                  size="sm"
                  onClick={(event) => {
                    event.stopPropagation();
                    handleApprove(row.federationId);
                  }}
                >
                  승인
                </InnocodeButton>
              ) : null}
              {row.rejectable ? (
                <InnocodeButton
                  size="sm"
                  theme="line"
                  designType="primary"
                  onClick={(event) => {
                    event.stopPropagation();
                    handleReject(row.federationId);
                  }}
                >
                  거절
                </InnocodeButton>
              ) : null}
            </div>
          ) : (
            <span className={styles.selectionMeta}>처리 완료</span>
          ),
      },
    ],
    [handleApprove, handleReject, resolveTenantLabel]
  );

  return (
    <div className={styles.pageWrapper}>
      <div>
        <h2 className={styles.pageTitle}>테넌트 연합 관리</h2>
      </div>

      <div className={styles.contentBox}>
        <div className={`${styles.panel} ${styles.rulePanel}`}>
          <p className={styles.ruleText}>
            연합 승인 후에도 서비스는 그룹에 자동 포함되지 않습니다. 연합은 테넌트
            간 승인 상태만 열어주며, 실제 서비스 포함은 별도 멤버 관리에서
            처리해야 합니다.
          </p>
        </div>

        <div className={styles.tableSection}>
          <InnocodeDatatable
            className={styles.dataTable}
            data={pagedFederations}
            columns={columns}
            enableCustomHeader={true}
            customHeader={
              <div className={styles.customHeaderContainer}>
                <div className={styles.customHeaderSearchContainer}>
                  <InnocodeInput
                    className={styles.searchInput}
                    value={sourceKeyword}
                    placeholder="요청자 테넌트 검색"
                    onChange={(event) => setSourceKeyword(event.target.value)}
                  />
                  <InnocodeInput
                    className={styles.searchInput}
                    value={targetKeyword}
                    placeholder="승인 대상 테넌트 검색"
                    onChange={(event) => setTargetKeyword(event.target.value)}
                  />
                  <div className={styles.dropdownWrapper}>
                    <InnocodeDropdown
                      value={status}
                      options={statusOptions}
                      onChange={(value) => setStatus(value || STATUS_ALL)}
                    />
                  </div>
                </div>
                <div className={styles.customHeaderActionContainer}>
                  <InnocodeButton
                    designType="primary"
                    theme="line"
                    size="md"
                    onClick={openRequestModal}
                  >
                    테넌트 연합 요청
                  </InnocodeButton>
                </div>
              </div>
            }
            enableTableHeader={true}
            enableSorting={false}
            enableCheckbox={false}
            enablePagination={true}
            pageSize={PAGE_SIZE}
            totalCount={filteredFederations.length}
            currentPage={page}
            onPageChange={setPage}
            emptyMessage="테넌트 연합 요청 목록이 없습니다."
          />
        </div>
      </div>
    </div>
  );
}

export default TenantFederationsPage;
